import * as React from 'react';
import Segmented from 'antd/lib/segmented';
import Button from 'antd/lib/button';
import Popover from 'antd/lib/popover';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { useLocation } from 'react-router-dom';
import { LayoutOutlined, TableOutlined, FilterOutlined } from '@ant-design/icons';
import CardListIcon from '@/components/icons/CardListIcon';
import { antGrey, tagStyle } from '@/components/styles/globalStyles';
import {
    PageViewType,
    TabularLayoutType,
    PageViewContext,
    CombinedLayoutValue,
    PAGE_VIEW_LABELS,
} from '@/types/summaryView';
export { PageViewContext };
import { FiltersPopover } from './FiltersPopover';
import { DestinationSelector } from '@/components/appHome/DestinationSelector';
import { SummaryViewContextValue } from '@/utils/hooks/useSummaryView';
import { parseListParam, getCombinedLayoutValue, handleCombinedLayoutChange } from '@/helpers/summaryViewHelpers';

interface OverviewTableHeaderProps {
    context?: PageViewContext;
    summary: SummaryViewContextValue;
    onTrackFilterApply?: (metrics: string[], versions: string[]) => void;
}

export const OverviewTableHeader: React.FC<OverviewTableHeaderProps> = ({
    context = PageViewContext.AppHome,
    summary,
    onTrackFilterApply,
}) => {
    const location = useLocation();
    const {
        preferences,
        updatePreferences,
        availableDestinations,
        availableMetrics,
        selectableVersions,
        handleFilterApply,
        handleDestinationChange,
        handlePageViewChange,
        handleTabularLayoutChange,
        getStoredPreferences,
        loading: { isLoading },
        pagination: { destinationLimit, requestedDestinationIds },
    } = summary;

    const { pageView, tabularLayout, selectedMetricKeys, selectedVersions, selectedDestinationIds } = preferences;
    const [filtersVisible, setFiltersVisible] = React.useState(false);
    const isTabularView = pageView === PageViewType.Tabular;
    const showDestinationSelector =
        availableDestinations.length > 1 && (context === PageViewContext.AppHome || isTabularView);

    const defaultFilters = React.useMemo(() => {
        const stored = getStoredPreferences();
        const urlParams = new URLSearchParams(location.search);

        const metricsFromStorage = stored.selectedMetrics;
        const metricsFromUrl = parseListParam(urlParams.get('metrics'));
        const defaultMetrics = metricsFromStorage?.length ? metricsFromStorage : metricsFromUrl;

        const versionsFromStorage = stored.selectedVersions;
        const versionsFromUrl = parseListParam(urlParams.get('versions'));
        const defaultVersions = versionsFromStorage?.length ? versionsFromStorage : versionsFromUrl;

        return { defaultMetrics, defaultVersions };
    }, [getStoredPreferences, location.search]);

    const labels = PAGE_VIEW_LABELS[context];
    const combinedLayoutValue = getCombinedLayoutValue(pageView, tabularLayout);
    const isAppHome = context === PageViewContext.AppHome;

    const renderAppHomeLayoutToggle = () => (
        <Segmented
            value={combinedLayoutValue}
            onChange={(value) => handleCombinedLayoutChange(value as CombinedLayoutValue, updatePreferences)}
            style={{ backgroundColor: antGrey.grey3 }}
            options={[
                {
                    value: 'summary' as CombinedLayoutValue,
                    icon: (
                        <Tooltip title="Summary">
                            <span>
                                <CardListIcon size={14} />
                            </span>
                        </Tooltip>
                    ),
                },
                {
                    value: 'standard' as CombinedLayoutValue,
                    icon: (
                        <Tooltip title="Standard table">
                            <LayoutOutlined style={{ color: antGrey.grey7 }} />
                        </Tooltip>
                    ),
                },
                {
                    value: 'compact' as CombinedLayoutValue,
                    icon: (
                        <Tooltip title="Compact table">
                            <TableOutlined style={{ color: antGrey.grey7 }} />
                        </Tooltip>
                    ),
                },
            ]}
        />
    );

    const renderOrgOverviewTabularLayoutToggle = () => (
        <Segmented
            value={tabularLayout}
            onChange={(value) => handleTabularLayoutChange(value as TabularLayoutType)}
            style={{ backgroundColor: antGrey.grey3 }}
            options={[
                {
                    value: TabularLayoutType.Standard,
                    icon: (
                        <Tooltip title="Standard table">
                            <LayoutOutlined style={{ color: antGrey.grey7 }} />
                        </Tooltip>
                    ),
                },
                {
                    value: TabularLayoutType.Compact,
                    icon: (
                        <Tooltip title="Compact table">
                            <TableOutlined style={{ color: antGrey.grey7 }} />
                        </Tooltip>
                    ),
                },
            ]}
        />
    );

    const renderFiltersButton = () => (
        <Popover
            overlayClassName="filters-overlay"
            destroyTooltipOnHide
            content={
                <FiltersPopover
                    isVisible={filtersVisible}
                    onApply={(metrics, versions) => {
                        setFiltersVisible(false);
                        handleFilterApply(metrics, versions);
                    }}
                    onTrack={onTrackFilterApply}
                    currentMetrics={selectedMetricKeys}
                    currentVersions={selectedVersions}
                    availableMetrics={availableMetrics}
                    availableVersions={selectableVersions}
                    isSubmitting={isLoading}
                    defaultMetrics={defaultFilters.defaultMetrics}
                    defaultVersions={defaultFilters.defaultVersions}
                />
            }
            title={<Text style={{ ...tagStyle, fontSize: 12, color: antGrey.grey7, padding: 15 }}>Filters</Text>}
            trigger="click"
            open={filtersVisible}
            placement="bottomRight"
            onOpenChange={setFiltersVisible}
        >
            <Button icon={<FilterOutlined />} loading={isLoading} disabled={isLoading}>
                Filters ({selectedMetricKeys.length} {selectedMetricKeys.length === 1 ? 'attribute' : 'attributes'},{' '}
                {selectedVersions.length} {selectedVersions.length === 1 ? 'version' : 'versions'})
            </Button>
        </Popover>
    );

    return (
        <div
            style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {!isAppHome && (
                    <Segmented
                        value={pageView}
                        onChange={(value) => handlePageViewChange(value as PageViewType)}
                        style={{ backgroundColor: antGrey.grey3, padding: 3 }}
                        options={[
                            {
                                label: (
                                    <span
                                        style={{
                                            padding: '10px 0',
                                            display: 'inline-block',
                                            fontSize: 16,
                                            fontWeight: 600,
                                            width: 120,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {labels.default}
                                    </span>
                                ),
                                value: PageViewType.Default,
                            },
                            {
                                label: (
                                    <span
                                        style={{
                                            padding: '10px 0',
                                            display: 'inline-block',
                                            fontSize: 16,
                                            fontWeight: 600,
                                            width: 120,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {labels.tabular}
                                    </span>
                                ),
                                value: PageViewType.Tabular,
                            },
                        ]}
                    />
                )}
                {showDestinationSelector && (
                    <DestinationSelector
                        destinations={availableDestinations}
                        selectedDestinationIds={selectedDestinationIds}
                        onSelectionChange={handleDestinationChange}
                        isLoading={isLoading}
                        requiresConfirmation
                        disabled={isLoading}
                        maxAddable={destinationLimit}
                        loadedDestinationIds={requestedDestinationIds}
                    />
                )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isTabularView && renderFiltersButton()}
                {isAppHome ? renderAppHomeLayoutToggle() : isTabularView && renderOrgOverviewTabularLayoutToggle()}
            </div>
        </div>
    );
};
